import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTable } from 'react-table';
import { format } from 'date-fns';

import { SUBSCRIPTION_INVOICES_QUERY } from '../queries';
import ErrorText from './ErrorText';
import LoadingSpinner from './LoadingSpinner';
import { capitalize, currency } from '../utils';
import ExternalThemeLink from './ExternalThemeLink';

function InvoicesTable({ data }) {
  const columns = useMemo(
    () => [
      {
        Header: 'Invoice Date',
        accessor: 'date',
        Cell: ({ value }) =>
          value ? format(new Date(Number(value)), 'PPP') : '',
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => capitalize(value),
      },
      {
        Header: 'Total',
        accessor: 'total',
        Cell: ({ value }) => currency(value),
      },
      {
        Header: 'Amount Paid',
        accessor: 'amountPaid',
        Cell: ({ value }) => currency(value),
      },
      {
        Header: 'Receipt',
        accessor: 'receiptUrl',
        Cell: ({ value }) => (
          <ExternalThemeLink href={value} rel="noreferrer" target="_blank">
            Receipt
          </ExternalThemeLink>
        ),
      },
    ],
    []
  );

  const tableInstance = useTable({
    columns,
    data,
  });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div className="h-full pb-6 overflow-auto">
      <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  className="border-b-2 border-black p-2 hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    {column.render('Header')}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td className="p-2" {...cell.getCellProps()}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="text-center pt-6">You don't have any invoices.</p>
      )}
    </div>
  );
}

export default function SubscriptionInvoicesTable() {
  const { loading, error, data } = useQuery(SUBSCRIPTION_INVOICES_QUERY);
  const invoices = data?.subscriptionInvoices;

  return (
    <div className="w-full flex flex-col">
      {loading && (
        <LoadingSpinner message="Getting your invoices..." className="mt-10" />
      )}
      {!loading && error && (
        <ErrorText message={error.message} className="ml-0" />
      )}
      {!loading && !error && invoices && <InvoicesTable data={invoices} />}
    </div>
  );
}
